import { mdiChevronDown, mdiChevronRight, mdiUnfoldLessHorizontal, mdiUnfoldMoreHorizontal } from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import * as React from 'react';
import {
    cellIntensity,
    IHeatmapCell,
    IHeatmapStatusRow,
    readableCellPaint,
    WEEK_COLUMNS,
    weekLabel,
    weekRange,
    WORKFLOW_LABELS,
    WORKFLOW_ORDER,
} from '../../services/worker-report/worker-report.model';
import { WorkerReportStore } from '../../stores/worker-report/worker-report.store';
import { Spinner } from '../base/spinner/spinner';
import { Variables } from '../base/variables/variables';
import { WORKFLOW_HUES, withAlpha } from './workflow-colors';
import * as S from './workflows.styles';

const WEEKS = Array.from({ length: WEEK_COLUMNS }, (_unused, index) => index + 1);
const formatCount = (count: number): string => count.toLocaleString();

interface ICellTipProps {
    title: string;
    week: number;
    count: number;
    description?: string;
    lines: { label: string; count: number; color?: string }[];
}

/**
 * A function, not a component: the core Tooltip hands its title to MUI's `Fade`, which needs a DOM node
 * through the ref. A function component has none, and the transition throws on `scrollTop` of null.
 */
const cellTip = ({ title, week, count, description, lines }: ICellTipProps): React.ReactElement => (
    <S.Tip>
        <S.TipTitle>{title}</S.TipTitle>
        {description && <S.TipMuted>{description}</S.TipMuted>}
        <S.TipMuted>
            {weekLabel(week)} ({weekRange(week)}) · {formatCount(count)} claims
        </S.TipMuted>
        {lines.map(line => (
            <S.TipLine key={line.label}>
                <span>
                    {line.color && <S.Swatch color={line.color} />}
                    {line.label}
                </span>
                <span>{formatCount(line.count)}</span>
            </S.TipLine>
        ))}
    </S.Tip>
);

interface IHeatCellProps {
    cell: IHeatmapCell;
    max: number;
    selected: boolean;
    tip: React.ReactElement;
    label: string;
    onOpen: () => void;
}

/** One box: the dominant workflow's hue at a volume-scaled strength, a wedge for a second workflow. */
const HeatCell: React.FC<IHeatCellProps> = ({ cell, max, selected, tip, label, onOpen }) => {
    if (!cell.count || !cell.dominant) {
        return <S.EmptyCell aria-hidden />;
    }
    const hue = WORKFLOW_HUES[cell.dominant];
    // White or dark text by the fill's real contrast, and a lighter shade where neither reaches 4.5:1.
    const paint = readableCellPaint(
        hue,
        cellIntensity(cell.count, max),
        Variables.ThemeLight.TextStrong,
        Variables.Swatches.White,
    );

    return (
        <S.GridTip direction="top" variant="light" title={tip}>
            <S.Cell
                variant="unstyled"
                fill={withAlpha(hue, paint.alpha)}
                wedge={cell.wedge ? WORKFLOW_HUES[cell.wedge] : undefined}
                invert={paint.lightText}
                selected={selected}
                aria-label={`${label}, ${weekLabel(cell.week)}: ${cell.count} claims. Open claims.`}
                onClick={onOpen}>
                {formatCount(cell.count)}
            </S.Cell>
        </S.GridTip>
    );
};

const workflowLines = (cell: IHeatmapCell) =>
    WORKFLOW_ORDER.filter(workflow => cell.byWorkflow[workflow] > 0).map(workflow => ({
        label: WORKFLOW_LABELS[workflow],
        count: cell.byWorkflow[workflow],
        color: WORKFLOW_HUES[workflow],
    }));

export interface IWorkflowHeatmapProps {
    store: WorkerReportStore;
}

/**
 * Worker Activity: claim age by workflow status. Weeks 1–9 are drawn, as designed; anything older is
 * counted on the summary cards and said under the grid, so the two never disagree silently (NS-3).
 */
export const WorkflowHeatmap: React.FC<IWorkflowHeatmapProps> = observer(({ store }) => {
    const { rows, max } = store.heatmap;
    const box = store.box;
    const isSelected = (status: string, week: number, actionValue?: string): boolean =>
        !!box && box.status === status && box.week === week && box.action === actionValue;

    const statusRow = (row: IHeatmapStatusRow): React.ReactNode[] => {
        const expandable = row.actions.length > 0;
        const open = store.expanded.has(row.status);
        const nodes: React.ReactNode[] = [
            <S.GridTip key={`${row.status}-head`} direction="right" variant="light" title={row.description ? <S.Tip><S.TipTitle>{row.label}</S.TipTitle><S.TipMuted>{row.description}</S.TipMuted></S.Tip> : undefined}>
                <S.RowHeader
                    variant="unstyled"
                    depth={0}
                    expandable={expandable}
                    aria-label={expandable ? `${open ? 'Collapse' : 'Expand'} ${row.label}` : row.label}
                    onClick={expandable ? (): void => store.toggleStatus(row.status) : undefined}>
                    <S.ChevronSlot>{expandable && <Icon path={open ? mdiChevronDown : mdiChevronRight} size={0.6} />}</S.ChevronSlot>
                    <S.RowLabel>{row.label}</S.RowLabel>
                    {row.errors > 0 && <S.ErrorBadge>{formatCount(row.errors)} err</S.ErrorBadge>}
                </S.RowHeader>
            </S.GridTip>,
            ...row.cells.map(cell => (
                <HeatCell
                    key={`${row.status}-${cell.week}`}
                    cell={cell}
                    max={max}
                    label={row.label}
                    selected={isSelected(row.status, cell.week)}
                    onOpen={(): void => store.openBox({ status: row.status, week: cell.week })}
                    tip={cellTip({
                        title: row.label,
                        week: cell.week,
                        count: cell.count,
                        description: row.description,
                        lines:
                            row.actions.length > 1
                                ? row.actions
                                      .map(actionRow => ({ label: actionRow.label, count: actionRow.cells[cell.week - 1].count }))
                                      .filter(line => line.count > 0)
                                : workflowLines(cell),
                    })}
                />
            )),
            <S.TotalCell key={`${row.status}-total`} depth={0}>
                {formatCount(row.total)}
            </S.TotalCell>,
        ];

        if (open) {
            row.actions.forEach(actionRow => {
                nodes.push(
                    <S.GridTip key={`${row.status}-${actionRow.action}-head`} direction="right" variant="light" title={actionRow.description ? <S.Tip><S.TipTitle>{actionRow.label}</S.TipTitle><S.TipMuted>{actionRow.description}</S.TipMuted></S.Tip> : undefined}>
                        <S.RowHeader variant="unstyled" depth={1} expandable={false} aria-label={actionRow.label}>
                            <S.RowLabel>{actionRow.label}</S.RowLabel>
                        </S.RowHeader>
                    </S.GridTip>,
                    ...actionRow.cells.map(cell => (
                        <HeatCell
                            key={`${row.status}-${actionRow.action}-${cell.week}`}
                            cell={cell}
                            max={max}
                            label={`${row.label} / ${actionRow.label}`}
                            selected={isSelected(row.status, cell.week, actionRow.action)}
                            onOpen={(): void => store.openBox({ status: row.status, week: cell.week, action: actionRow.action })}
                            tip={cellTip({
                                title: `${row.label} / ${actionRow.label}`,
                                week: cell.week,
                                count: cell.count,
                                description: actionRow.description,
                                lines: workflowLines(cell),
                            })}
                        />
                    )),
                    <S.TotalCell key={`${row.status}-${actionRow.action}-total`} depth={1}>
                        {formatCount(actionRow.total)}
                    </S.TotalCell>,
                );
            });
        }

        return nodes;
    };

    const body = (): React.ReactNode => {
        if (store.loading && !store.report) {
            return (
                <S.Message>
                    <Spinner size="16px" />
                    Loading the worker report. This can take a few seconds.
                </S.Message>
            );
        }
        if (store.error) {
            return <S.Message>The worker report could not be loaded, so these counts are unknown.</S.Message>;
        }
        if (!store.report) {
            return null;
        }
        if (!store.report.groups.length) {
            return <S.Message>No active claims changed worker state in the last 365 days.</S.Message>;
        }
        if (!rows.length) {
            return <S.Message>No claims in weeks 1–{WEEK_COLUMNS} match these filters.</S.Message>;
        }

        return rows.flatMap(statusRow);
    };

    const expandable = rows.some(row => row.actions.length > 0);

    return (
        <S.Grid role="grid" aria-label="Worker activity by workflow status and week">
            <S.HeaderCell align="left">
                Workflow status
                {expandable && (
                    <S.ExpandAll variant="text" size="small" onClick={store.toggleExpandAll}>
                        <Icon path={store.allExpanded ? mdiUnfoldLessHorizontal : mdiUnfoldMoreHorizontal} size={0.55} color={Variables.ThemeLight.PrimaryConstant6} />
                        {store.allExpanded ? 'Collapse all' : 'Expand all'}
                    </S.ExpandAll>
                )}
            </S.HeaderCell>
            {WEEKS.map(week => (
                <S.GridTip key={week} direction="top" variant="light" title={<S.Tip><S.TipMuted>{weekRange(week)} since the last worker state change</S.TipMuted></S.Tip>}>
                    <S.HeaderCell>{weekLabel(week)}</S.HeaderCell>
                </S.GridTip>
            ))}
            <S.HeaderCell align="right">Total</S.HeaderCell>
            {body()}
        </S.Grid>
    );
});
